import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface KlingEffectsMultiV15Params {
  /**
   * 输入参数
   */
  input: {
    /**
     * 输入图像URL列表，必须包含2个图像
     */
    images: string[];
    /**
     * 视频时长，必须为正数
     */
    duration: number;
  };
  /**
   * 特效场景，可选值：'hug'、'kiss'、'heart_gesture'
   * @values hug | kiss | heart_gesture
   */
  effect_scene: 'hug' | 'kiss' | 'heart_gesture';
  /**
   * 外部任务ID
   */
  external_task_id?: string;
}

export interface KlingEffectsMultiV15Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * kling_effects_multi_v15
 *
 * Kling AI Multi Person Video Effects Generator (V1.5 Model)
 * 
 * Multi person video effects generation implementation for Kling AI API using V1.5 model.
 * 
 * Model: kling-v1-5 (固定模型)
 * Features: 多人视频特效生成功能
 * Effect Scenes: hug（拥抱）、kiss（亲吻）、heart_gesture（比心）
 * Input: 必须提供2张输入图像URL
 * Duration: 必须指定正数视频时长
 * Advanced: 支持回调URL通知、外部任务ID关联
 *
 * @example
 * ```typescript
 * import { initSeacloud, klingEffectsMultiV15 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await klingEffectsMultiV15({
 *   // 参数见 KlingEffectsMultiV15Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 KlingEffectsMultiV15Params 接口定义
 * @returns 生成的资源列表
 */
export async function klingEffectsMultiV15(
  params: KlingEffectsMultiV15Params,
): Promise<KlingEffectsMultiV15Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'kling_effects_multi_v15',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: KlingEffectsMultiV15Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
